/**
 * MontaTech v2 - Grouping Algorithm
 *
 * Agrupa objetos identicos (incluindo copias rotacionadas) para o preview.
 * Replica a logica do C# BuildGroupingKey() em Json2DCanvasRenderer.cs.
 *
 * Chave: min(W,H)|max(W,H)|MaxLength|pathCount|segmentCount|topology
 */

/**
 * Interface minima para agrupamento.
 * Compativel com RCurve (lib) e MontaTechCurve (component types).
 */
export interface GroupableCurve {
  width: number;
  height: number;
  maxLength: number;
  paths: Array<{
    closed?: boolean;
    segments?: Array<unknown> | null;
  }> | null;
}

export interface GroupedCurve<T extends GroupableCurve = GroupableCurve> {
  /** Curva representante do grupo */
  curve: T;
  /** Quantidade de copias identicas */
  quantity: number;
  /** Chave de agrupamento (para debug) */
  key: string;
}

/**
 * Formata um numero para a chave de agrupamento com precisao fixa.
 */
function formatForKey(value: number, precision: number): string {
  return value.toFixed(precision);
}

/**
 * Gera a chave de agrupamento rotation-invariant para uma curva.
 *
 * Usa min/max das dimensoes (para cobrir rotacoes 0/90/180/270)
 * + topologia (pathCount, segmentCount, closed/segCount por path).
 */
export function buildGroupingKey(curve: GroupableCurve, precision = 2): string {
  const safePrecision = Math.max(0, Math.min(6, precision));
  const pathCount = curve.paths?.length ?? 0;
  let segmentCount = 0;
  let topology = '';

  if (curve.paths) {
    for (const path of curve.paths) {
      const segCount = path.segments?.length ?? 0;
      segmentCount += segCount;
      topology += `${path.closed ? '1' : '0'}:${segCount};`;
    }
  }

  const dimA = Math.min(curve.width, curve.height);
  const dimB = Math.max(curve.width, curve.height);

  return [
    formatForKey(dimA, safePrecision),
    formatForKey(dimB, safePrecision),
    formatForKey(curve.maxLength, safePrecision),
    pathCount.toString(),
    segmentCount.toString(),
    topology
  ].join('|');
}

/**
 * Agrupa curvas identicas (mesmas dimensoes + topologia).
 * Retorna array de grupos ordenados pela primeira ocorrencia.
 */
export function groupCurves<T extends GroupableCurve>(curves: T[], precision = 2): GroupedCurve<T>[] {
  const map = new Map<string, GroupedCurve<T>>();
  const items: GroupedCurve<T>[] = [];

  for (const curve of curves) {
    const key = buildGroupingKey(curve, precision);
    const existing = map.get(key);

    if (existing) {
      existing.quantity++;
      continue;
    }

    const item: GroupedCurve = { curve, quantity: 1, key };
    map.set(key, item);
    items.push(item);
  }

  return items;
}
